import type { Bounding } from 'klinecharts';

// ── Label constants ───────────────────────────────────────────────────────────
const FONT_SIZE = 8;
const PAD_X     = 5;
const PAD_Y     = 2;
const RADIUS    = 3;
const LINE_GAP  = 2;

// ── Types ────────────────────────────────────────────────────────────────────

export type ScanLabelOffset = { dx: number; dy: number };

export type ScanLabelLayout = {
  key: string;
  index: number;
  x: number;
  y: number;
  w: number;
  h: number;
  anchorX: number;
  anchorY: number;
  color: string;
  closeBox?: { x: number; y: number; w: number; h: number };
};

export interface ScanLabelConfig<T> {
  /** Returns true when this bar should show a label */
  condition: (result: T, index: number) => boolean;
  /** Label text to display, e.g. "51M  +30%" */
  format: (result: T, index: number) => string | string[];
  /** Indicator-unit Y value to anchor the label above/below. Return null to skip. */
  anchorValue: (result: T, index: number) => number | null;
  /** Hex color for border + text (or a resolver per row) */
  color: string | ((result: T, index: number) => string);
  /** Stable key used to persist offsets or dismissals */
  getKey?: (result: T, index: number) => string;
  /** Optional offset in pixels for this label */
  getOffset?: (result: T, index: number) => ScanLabelOffset;
  /** 'above' anchors label above the value (default), 'below' anchors below */
  position?: 'above' | 'below';
  /** Optional dashed leader line from label to anchor */
  leaderLine?: {
    enabled?: boolean;
    dash?: number[];
    width?: number;
    alpha?: number;
  };
  /** Optional close icon rendered inside the label */
  closeIcon?: { enabled?: boolean; size?: number; padding?: number };
  /** Optional top padding inside the pane to avoid overlay collisions */
  topPadding?: number;
  /** Optional callback to expose final label layout boxes */
  onLayout?: (items: ScanLabelLayout[]) => void;
}

// ── Core drawing function ────────────────────────────────────────────────────

/**
 * Draws floating pill labels on the canvas for any bar that satisfies
 * `config.condition`. Clamps labels to the pane top boundary and nudges
 * vertically on adjacent bars to avoid overlaps.
 *
 * Call this inside an indicator's `draw()` function after destructuring
 * ctx / visibleRange / xAxis / yAxis / bounding from params.
 */
export function drawScanLabels<T>(
  ctx: CanvasRenderingContext2D,
  results: T[],
  visibleRange: { from: number; to: number },
  xAxis: { convertToPixel: (i: number) => number },
  yAxis: { convertToPixel: (v: number) => number },
  bounding: Bounding,
  config: ScanLabelConfig<T>,
): ScanLabelLayout[] {
  const { from, to } = visibleRange;
  const above = (config.position ?? 'above') === 'above';

  ctx.save();
  ctx.font         = `bold ${FONT_SIZE}px sans-serif`;
  ctx.textBaseline = 'middle';
  ctx.textAlign    = 'center';

  const minY = Math.max(2, config.topPadding ?? 2);
  const placed: Array<{ x1: number; x2: number; y: number; h: number }> = [];
  const layouts: ScanLabelLayout[] = [];

  const maxYFor = (h: number) => Math.max(minY, bounding.height - h - 2);
  const clampY = (y: number, h: number) => Math.min(maxYFor(h), Math.max(minY, y));
  const overlaps = (x1: number, x2: number, y: number, h: number) =>
    placed.some((p) => !(x2 < p.x1 || x1 > p.x2 || (y + h + 2) < p.y || y > (p.y + p.h + 2)));

  for (let i = from; i <= to; i++) {
    const r = results[i];
    if (r == null || !config.condition(r, i)) continue;

    const anchor = config.anchorValue(r, i);
    if (anchor == null) continue;

    const rawText   = config.format(r, i);
    const lines     = Array.isArray(rawText) ? rawText : String(rawText).split('\n');
    const widths    = lines.map((line) => ctx.measureText(line).width);
    const textWidth = widths.length ? Math.max(...widths) : 0;
    const textH     = lines.length * FONT_SIZE + Math.max(0, lines.length - 1) * LINE_GAP;
    const boxW      = textWidth + PAD_X * 2;
    const boxH      = textH + PAD_Y * 2;
    const cx        = xAxis.convertToPixel(i);

    // Skip labels for bars outside the visible pane — avoids ghost boxes at the left edge
    if (cx < 0 || (bounding.width && cx > bounding.width)) continue;

    const anchorPx  = yAxis.convertToPixel(anchor);
    const offset    = config.getOffset?.(r, i) ?? { dx: 0, dy: 0 };
    const hasOffset = offset.dx !== 0 || offset.dy !== 0;
    let boxX        = cx - boxW / 2 + offset.dx;

    // Default Y placement
    let desiredY = above
      ? anchorPx - boxH - 4
      : anchorPx + 4;
    desiredY += offset.dy;

    // If an above-label would get clamped into the header area, fall back
    // to placing it just below the bar instead of stacking at the top.
    if (above && desiredY < minY) {
      desiredY = Math.min(maxYFor(boxH), anchorPx + 4);
    }

    let boxY = clampY(desiredY, boxH);

    if (bounding.width) {
      const minX = 2;
      const maxX = Math.max(minX, bounding.width - boxW - 2);
      boxX = Math.min(maxX, Math.max(minX, boxX));
    }

    if (!hasOffset && overlaps(boxX, boxX + boxW, boxY, boxH)) {
      const maxSteps = 6;
      let found = false;
      for (let s = 1; s <= maxSteps; s++) {
        const step = boxH + 2;
        const up = clampY(desiredY - step * s, boxH);
        if (!overlaps(boxX, boxX + boxW, up, boxH)) { boxY = up; found = true; break; }

        const down = clampY(desiredY + step * s, boxH);
        if (!overlaps(boxX, boxX + boxW, down, boxH)) { boxY = down; found = true; break; }
      }
      if (!found) boxY = clampY(desiredY, boxH);
    }

    placed.push({ x1: boxX, x2: boxX + boxW, y: boxY, h: boxH });

    const color = typeof config.color === 'function'
      ? config.color(r, i)
      : config.color;
    const key = config.getKey ? config.getKey(r, i) : String(i);

    // ── Optional leader line ─────────────────────────────────────────────
    if (config.leaderLine?.enabled) {
      const lineY = above ? boxY + boxH : boxY;
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth   = config.leaderLine.width ?? 1;
      ctx.globalAlpha = config.leaderLine.alpha ?? 0.5;
      ctx.setLineDash(config.leaderLine.dash ?? [3, 3]);
      ctx.beginPath();
      ctx.moveTo(cx, anchorPx);
      ctx.lineTo(cx, lineY);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }

    // ── Rounded rect background ───────────────────────────────────────────
    ctx.beginPath();
    ctx.moveTo(boxX + RADIUS, boxY);
    ctx.lineTo(boxX + boxW - RADIUS, boxY);
    ctx.quadraticCurveTo(boxX + boxW, boxY, boxX + boxW, boxY + RADIUS);
    ctx.lineTo(boxX + boxW, boxY + boxH - RADIUS);
    ctx.quadraticCurveTo(boxX + boxW, boxY + boxH, boxX + boxW - RADIUS, boxY + boxH);
    ctx.lineTo(boxX + RADIUS, boxY + boxH);
    ctx.quadraticCurveTo(boxX, boxY + boxH, boxX, boxY + boxH - RADIUS);
    ctx.lineTo(boxX, boxY + RADIUS);
    ctx.quadraticCurveTo(boxX, boxY, boxX + RADIUS, boxY);
    ctx.closePath();

    ctx.globalAlpha = 0.20;
    ctx.fillStyle   = color;
    ctx.fill();
    ctx.globalAlpha = 1;

    ctx.strokeStyle = color;
    ctx.lineWidth   = 1;
    ctx.stroke();

    // ── Close icon ─────────────────────────────────────────────────────
    let closeBox: ScanLabelLayout['closeBox'];
    if (config.closeIcon?.enabled) {
      const closeSize = config.closeIcon.size ?? 7;
      const padding   = config.closeIcon.padding ?? 2;
      const closeX    = boxX + boxW - padding - closeSize / 2;
      const closeY    = boxY + padding + closeSize / 2;
      closeBox = {
        x: boxX + boxW - padding - closeSize,
        y: boxY + padding,
        w: closeSize,
        h: closeSize,
      };
      ctx.save();
      ctx.font = `bold ${closeSize}px sans-serif`;
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('x', closeX, closeY);
      ctx.restore();
    }

    // ── Text ──────────────────────────────────────────────────────────────
    ctx.fillStyle = color;
    if (lines.length <= 1) {
      ctx.fillText(lines[0] ?? '', cx, boxY + boxH / 2);
    } else {
      const firstLineY = boxY + PAD_Y + FONT_SIZE / 2;
      lines.forEach((line, idx) => {
        const y = firstLineY + idx * (FONT_SIZE + LINE_GAP);
        ctx.fillText(line, cx, y);
      });
    }

    layouts.push({
      key,
      index: i,
      x: boxX,
      y: boxY,
      w: boxW,
      h: boxH,
      anchorX: cx,
      anchorY: anchorPx,
      color,
      closeBox,
    });
  }

  ctx.restore();
  config.onLayout?.(layouts);
  return layouts;
}

// ── Format helpers ────────────────────────────────────────────────────────────

export function fmtVolume(v: number): string {
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`;
  if (v >= 1_000_000)     return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)         return `${(v / 1_000).toFixed(0)}K`;
  return String(Math.round(v));
}

export function fmtPct(v: number, decimals = 0): string {
  return `${v >= 0 ? '+' : ''}${v.toFixed(decimals)}%`;
}
